export interface PartnerSite {
  /** Name of the program, school, clinic, or project on the ground */
  name: string
  nameEs?: string
  /** Human-readable place, e.g. 'San Pedro Sula, Honduras' */
  location: string
  /** [latitude, longitude] of the field site */
  coords: [number, number]
}

/**
 * Isolates a logo from a busier source image (such as a screenshot).
 * `region` is [left, top, width, height] as percentages of the source image;
 * `aspect` is that region's width ÷ height; `mask` is an optional CSS
 * clip-path that trims the region to the logo's own outline.
 */
export interface LogoCrop {
  region: [number, number, number, number]
  aspect: number
  mask?: string
}

export interface SisterOrganization {
  name: string
  location: string
  focus: string
  focusEs: string
  description: string
  descriptionEs: string
  url?: string
  /** [latitude, longitude] of the organization's primary base */
  coords: [number, number]
  /** Hex accent used for the map pin and the card's top stripe */
  accent: string
  /** Square logo URL. Organizations with a logo appear in the expandable spotlight list. */
  logo?: string
  /** Optional crop/mask applied to the logo image */
  logoCrop?: LogoCrop
  /**
   * Fallback zoom for logos with heavy built-in padding, used only if the
   * padding can't be trimmed automatically. e.g. 1.5
   */
  logoScale?: number
  /** Optional CSS filter, e.g. to turn an off-white logo background white */
  logoFilter?: string
  /** The region this organization serves from its U.S. base */
  serviceArea?: string
  serviceAreaEs?: string
  /** Mission statement, shown when the spotlight card is expanded */
  mission?: string
  missionEs?: string
  /** Set to false to hide the "Registered 501(c)(3)" badge (defaults to shown) */
  registered501c3?: boolean
  /** Optional donate/get-involved link shown as a button on the spotlight card */
  donateUrl?: string
  /** Leadership shown alongside the mission */
  director?: {
    name?: string
    title: string
    titleEs: string
    photo: string
    linkedin?: string
    /** Optional link to the person's HAA member profile */
    profileUrl?: string
  }
  /**
   * Optional sites in Honduras (or elsewhere) where this organization works.
   * The map draws a dashed arc from the U.S. base to each one, and the
   * detail card offers a zoom action per site.
   */
  partnerSites?: PartnerSite[]
}

/**
 * Participating organizations in the Honduran-American National Network.
 *
 * Add each organization as it joins — every entry automatically appears both
 * as a pin on the network map and as a row in the directory.
 *
 * Template:
 * {
 *   name: 'CRC — We Care We Share',
 *   location: 'New Orleans, LA',
 *   focus: 'Community Services',
 *   focusEs: 'Servicios Comunitarios',
 *   description: 'One or two sentences about the organization.',
 *   descriptionEs: 'Una o dos oraciones sobre la organización.',
 *   url: 'https://www.crcwecareweshare.org/',
 *   coords: [29.9511, -90.0715],
 *   accent: '#1565C0',
 *   partnerSites: [
 *     {
 *       name: 'Community Programs',
 *       nameEs: 'Programas Comunitarios',
 *       location: 'San Pedro Sula, Honduras',
 *       coords: [15.5041, -88.025],
 *     },
 *   ],
 * }
 */
export const SISTER_ORGANIZATIONS: SisterOrganization[] = [
  {
    name: 'Leadership Mission International',
    location: 'Seattle, WA',
    focus: "Women's Education & Leadership",
    focusEs: 'Educación y Liderazgo de Mujeres',
    description:
      'Operates The Leadership Center, a residential campus in Honduras offering young women a two-year certification in English, business, leadership, and community development.',
    descriptionEs:
      'Opera The Leadership Center, un campus residencial en Honduras que ofrece a mujeres jóvenes una certificación de dos años en inglés, negocios, liderazgo y desarrollo comunitario.',
    mission:
      'To develop a new generation of ethical women leaders who drive positive change—through exceptional academic education, leadership training, alumnae support, and community development experiences.',
    missionEs:
      'Formar una nueva generación de mujeres líderes éticas que impulsen el cambio positivo—a través de una educación académica excepcional, formación en liderazgo, apoyo a egresadas y experiencias de desarrollo comunitario.',
    url: 'https://www.leadershipmission.org/',
    donateUrl: 'https://www.leadershipmission.org/give',
    logo: 'https://cdn.magicpatterns.com/uploads/qcabvtvwbwGxePYnDwzR3R/Leadership_Mission.webp',
    director: {
      name: 'Gris Bailey',
      title: 'Executive Director',
      titleEs: 'Directora Ejecutiva',
      photo:
        'https://cdn.magicpatterns.com/uploads/jR2yMvWY3ZNjunoK78bnu1/Gris_Bailey.png',
      linkedin: 'https://www.linkedin.com/in/gknetwork/',
    },
    coords: [47.6062, -122.3321],
    accent: '#cf142b',
    partnerSites: [
      {
        name: 'The Leadership Center',
        location: 'Comayagua, Honduras',
        coords: [14.4514, -87.6375],
      },
    ],
  },
  {
    name: 'ASJ — Association for a More Just Society',
    location: 'Grand Rapids, MI',
    focus: 'Justice & Anti-Corruption',
    focusEs: 'Justicia y Anticorrupción',
    description:
      'A faith-based organization working in partnership with ASJ-Honduras, a team of Hondurans confronting violence and corruption, while inspiring people around the world to seek justice in their own communities.',
    descriptionEs:
      'Una organización basada en la fe que trabaja en alianza con ASJ-Honduras, un equipo de hondureños que enfrenta la violencia y la corrupción, e inspira a personas en todo el mundo a buscar la justicia en sus propias comunidades.',
    mission:
      'To do justice in Honduras and to inspire others around the world to seek justice in their own contexts—working hand in hand with ASJ-Honduras to stop violence and corruption.',
    missionEs:
      'Hacer justicia en Honduras e inspirar a otros en todo el mundo a buscar la justicia en sus propios contextos—trabajando de la mano con ASJ-Honduras para detener la violencia y la corrupción.',
    url: 'https://www.asj-us.org/',
    donateUrl: 'https://www.asj-us.org/?give=8NDR96EK',
    logo: 'https://cdn.magicpatterns.com/uploads/agztEz1jFBg7ZWccmprW8A/ASJ_Logo_USA.webp',
    director: {
      name: 'Kyle Meyaard-Schaap',
      title: 'Executive Director',
      titleEs: 'Director Ejecutivo',
      photo:
        'https://cdn.magicpatterns.com/uploads/jQsbxMQ33ZDnJoW6Xa969M/Kyle_Meyaard.webp',
    },
    coords: [42.9634, -85.6681],
    accent: '#0b1f3f',
    partnerSites: [
      {
        name: 'ASJ-Honduras',
        location: 'Tegucigalpa, Francisco Morazán',
        coords: [14.0723, -87.1921],
      },
    ],
  },
  {
    name: 'CRC We Care! We Share!',
    location: 'New Orleans, LA',
    focus: 'Housing, Nutrition & Education',
    focusEs: 'Vivienda, Nutrición y Educación',
    description:
      'A volunteer-run 501(c)(3) where 100% of donations go directly to those in need—building homes, providing food, supporting community development, and expanding education.',
    descriptionEs:
      'Una organización 501(c)(3) dirigida por voluntarios donde el 100% de las donaciones llega directamente a quienes lo necesitan—construyendo viviendas, entregando alimentos, apoyando el desarrollo comunitario y ampliando la educación.',
    mission:
      'To honor the duty of giving back—building homes, providing nutritious food, strengthening communities, and creating lasting educational opportunities, with no overhead and 100% of every donation reaching those in need.',
    missionEs:
      'Honrar el deber de retribuir—construyendo viviendas, proporcionando alimentos nutritivos, fortaleciendo comunidades y creando oportunidades educativas duraderas, sin gastos administrativos y con el 100% de cada donación llegando a quienes lo necesitan.',
    url: 'https://www.crcwecareweshare.org/',
    logo: 'https://cdn.magicpatterns.com/uploads/6G64QUxA6AeXsT7maVvc2n/CRC_WCWS-RGB.png',
    director: {
      name: 'Dr. Jenny Mains',
      title: 'Founder',
      titleEs: 'Fundadora',
      photo:
        'https://cdn.magicpatterns.com/uploads/kbPFGNz1eVKATCnhDUrwBU/Jenny_Mains.jpg',
      profileUrl: '#/member/jenny-mains',
    },
    coords: [29.9511, -90.0715],
    accent: '#1565C0',
    partnerSites: [
      {
        name: 'Sector Rivera Hernández',
        location: 'Sector Rivera Hernández, San Pedro Sula',
        coords: [15.4783, -87.9556],
      },
    ],
  },
  {
    name: 'Montaña de Luz',
    location: 'Worthington, OH',
    focus: 'HIV Care & Family Health',
    focusEs: 'Atención del VIH y Salud Familiar',
    description:
      "Serves children, youth, and families affected by HIV/AIDS in Honduras through a children's home, the Healthy Families Honduras program, and traveling HIV education.",
    descriptionEs:
      'Atiende a niños, jóvenes y familias afectados por el VIH/SIDA en Honduras a través de un hogar infantil, el programa Familias Saludables Honduras y educación itinerante sobre el VIH.',
    mission:
      'To empower children and families affected by HIV/AIDS with medicine and education—so young people in Honduras can grow up healthy, supported, and out of HIV’s shadow.',
    missionEs:
      'Empoderar a niños y familias afectados por el VIH/SIDA con medicina y educación—para que los jóvenes en Honduras crezcan sanos, acompañados y fuera de la sombra del VIH.',
    url: 'https://montanadeluz.org/',
    donateUrl: 'https://montanadeluz.org/give/',
    logo: 'https://cdn.magicpatterns.com/uploads/3DXyiLRxitbC3yuSuqFKz2/Montana_de_Luz.png',
    director: {
      name: 'Anna Jackson',
      title: 'Executive Director',
      titleEs: 'Directora Ejecutiva',
      photo:
        'https://cdn.magicpatterns.com/uploads/qKkjSJw5r7Ve5i8HnmWzWS/Anna_Jackson.jpg',
      linkedin: 'https://www.linkedin.com/in/anna-jackson-1a07ab111/',
    },
    coords: [40.0931, -83.018],
    accent: '#1565C0',
  },
  {
    name: 'One Thousand Schools',
    location: 'Stafford, VA',
    focus: 'Education & Youth Empowerment',
    focusEs: 'Educación y Empoderamiento Juvenil',
    description:
      'Building 1,000 schools across rural Honduras and training teachers, alongside the Villa Soleada community in El Progreso—with Honduran-led programs and community ownership at every step.',
    descriptionEs:
      'Construyendo 1,000 escuelas en las zonas rurales de Honduras y capacitando maestros, junto a la comunidad de Villa Soleada en El Progreso—con programas dirigidos por hondureños y participación comunitaria en cada paso.',
    mission:
      'To alleviate violence and extreme poverty in Honduras through education and youth empowerment.',
    missionEs:
      'Aliviar la violencia y la pobreza extrema en Honduras a través de la educación y el empoderamiento juvenil.',
    url: 'https://onethousandschools.com/',
    donateUrl: 'https://onethousandschools.com/donate/',
    logo: 'https://cdn.magicpatterns.com/uploads/tmxXJ8NojqV91jSqdNLAtw/One_Thousand_Schools.png',
    coords: [38.4221, -77.4083],
    accent: '#0b1f3f',
    partnerSites: [
      {
        name: 'Villa Soleada',
        location: 'El Progreso, Yoro',
        coords: [15.4003, -87.8003],
      },
    ],
  },
  {
    name: 'Friends of FUNDESUR',
    location: 'San Diego, CA',
    focus: 'Education, Health & Environment',
    focusEs: 'Educación, Salud y Medio Ambiente',
    description:
      "A 501(c)(3) extending FUNDESUR's 12-year record of education, health, environmental, and livelihood work across southern Honduras—from school building and clean water to mangrove restoration and sea turtle protection.",
    descriptionEs:
      'Una organización 501(c)(3) que amplía los 12 años de trayectoria de FUNDESUR en educación, salud, medio ambiente y medios de vida en el sur de Honduras—desde la construcción de escuelas y el acceso a agua potable hasta la restauración de manglares y la protección de tortugas marinas.',
    mission:
      'To mobilize relationships and resources to expand opportunity for communities across Honduras.',
    missionEs:
      'Movilizar relaciones y recursos para ampliar las oportunidades de las comunidades en todo Honduras.',
    url: 'https://friendsoffundesur.org/',
    donateUrl: 'https://friendsoffundesur.org/donate',
    logo: 'https://cdn.magicpatterns.com/uploads/mmCawAEc9kqqtfUDcsmAKS/Friends_of_Fundesur.png',
    director: {
      name: 'Maxie Gluckman',
      title: 'Executive Director',
      titleEs: 'Directora Ejecutiva',
      photo:
        'https://cdn.magicpatterns.com/uploads/krX6KuPbcZrX2MqegmnDhk/Maxie_Gluckman_ED.jpg',
      linkedin: 'https://www.linkedin.com/in/maxiegluckman/',
    },
    coords: [32.7157, -117.1611],
    accent: '#1565C0',
    partnerSites: [
      {
        name: 'FUNDESUR Community Programs',
        nameEs: 'Programas Comunitarios de FUNDESUR',
        location: 'Choluteca, Southern Honduras',
        coords: [13.3007, -87.1908],
      },
    ],
  },
  {
    name: 'ESPACIOH',
    registered501c3: false,
    location: 'Washington, DC',
    focus: 'Research, Democracy & Social Innovation',
    focusEs: 'Investigación, Democracia e Innovación Social',
    description:
      'A youth-driven think tank and nonprofit promoting academic research, democracy, and social innovation in Honduras—including its Revista Diplomática, an open platform for analysis on international relations.',
    descriptionEs:
      'Un centro de pensamiento y organización sin fines de lucro impulsado por jóvenes que promueve la investigación académica, la democracia y la innovación social en Honduras—incluida su Revista Diplomática, una plataforma abierta de análisis sobre relaciones internacionales.',
    mission:
      'To empower a new generation of Honduran thinkers—advancing academic research, democratic values, and social innovation projects that strengthen Honduras and its place in the world.',
    missionEs:
      'Empoderar a una nueva generación de pensadores hondureños—impulsando la investigación académica, los valores democráticos y proyectos de innovación social que fortalezcan a Honduras y su lugar en el mundo.',
    url: 'https://www.espaciohnd.org/',
    logo: 'https://cdn.magicpatterns.com/uploads/pEwgbSjKG13pEhRuXw9RZU/Espacio_H.jpg',
    // The source image has wide margins around the mark; enlarge it so it
    // reads at the same visual size as the other logos.
    logoScale: 1.6,
    director: {
      name: 'Álvaro Castillo',
      title: 'President & Founder',
      titleEs: 'Presidente y Fundador',
      photo:
        'https://cdn.magicpatterns.com/uploads/4t8JLzrE3U1v3tkjqGBLWH/Alvaro_Castillo.png',
      linkedin: 'https://www.linkedin.com/in/alvaro-castillo-72103b9b/',
    },
    coords: [38.9072, -77.0369],
    accent: '#0b1f3f',
    partnerSites: [
      {
        name: 'Research & Youth Programs',
        nameEs: 'Programas de Investigación y Juventud',
        location: 'Tegucigalpa, Francisco Morazán',
        coords: [14.0723, -87.1921],
      },
    ],
  },
  {
    name: 'Honduras Outreach Inc.',
    location: 'Alpharetta, GA',
    focus: 'Health, Education & Rural Development',
    focusEs: 'Salud, Educación y Desarrollo Rural',
    description:
      "A Christ-centered 501(c)(3) serving rural Honduras since 1989 across agriculture, community development, education, health, and spiritual life—including four clinics that serve 16,000 patients a year and 43 kindergartens built in the Agalta Valley.",
    descriptionEs:
      'Una organización 501(c)(3) centrada en Cristo que sirve a la Honduras rural desde 1989 en agricultura, desarrollo comunitario, educación, salud y vida espiritual—incluidas cuatro clínicas que atienden a 16,000 pacientes al año y 43 kínderes construidos en el Valle de Agalta.',
    mission:
      'To improve the quality of life for Honduran communities—through education, healthcare, agriculture, discipleship, and development that give families life-changing resources and sustainable opportunities.',
    missionEs:
      'Mejorar la calidad de vida de las comunidades hondureñas—a través de la educación, la salud, la agricultura, el discipulado y el desarrollo, brindando a las familias recursos transformadores y oportunidades sostenibles.',
    url: 'https://hoi.org/',
    logo: 'https://cdn.magicpatterns.com/uploads/pqx3mHHYmDUmSe4kDMz59W/Capa_1.webp',
    director: {
      name: 'Camila Reina',
      title: 'CEO',
      titleEs: 'Directora General',
      photo:
        'https://cdn.magicpatterns.com/uploads/tecVv9r1dG8T5UqTyqGDet/Camila_Reina.png',
      linkedin: 'https://www.linkedin.com/in/camila-reina-402a3847/',
    },
    coords: [34.0754, -84.2941],
    accent: '#1565C0',
    partnerSites: [
      {
        name: 'Agalta Valley Programs',
        nameEs: 'Programas del Valle de Agalta',
        location: 'Agalta Valley, Olancho',
        coords: [15.2167, -85.75],
      },
    ],
  },
  {
    name: 'Educate 2 Envision International',
    location: 'Oakland, CA',
    focus: 'Rural Education & Youth Leadership',
    focusEs: 'Educación Rural y Liderazgo Juvenil',
    description:
      'Transforming rural education in Honduras through affordable secondary schooling, youth-led community projects, and pathways into technical training, university, employment, and entrepreneurship—reaching 1,000 rural youth across five academies and ten communities.',
    descriptionEs:
      'Transforma la educación rural en Honduras mediante educación secundaria accesible, proyectos comunitarios liderados por jóvenes y rutas hacia la formación técnica, la universidad, el empleo y el emprendimiento—llegando a 1,000 jóvenes rurales en cinco academias y diez comunidades.',
    mission:
      'To reimagine rural education—believing lasting change begins with education, grows through leadership, and continues by creating pathways to opportunity.',
    missionEs:
      'Reimaginar la educación rural—convencidos de que el cambio duradero comienza con la educación, crece a través del liderazgo y continúa creando caminos hacia la oportunidad.',
    url: 'https://www.educate2envision.org/',
    donateUrl: 'https://www.educate2envision.org/donate/',
    logo: 'https://cdn.magicpatterns.com/uploads/9qbUEQd4bAgJLpfXTj1bDY/E2E.png',
    director: {
      name: 'Katia Gomez',
      title: 'Executive Director',
      titleEs: 'Directora Ejecutiva',
      photo:
        'https://cdn.magicpatterns.com/uploads/xnUXv56bELidWLx8aZnEcC/Katia_Gomez.jpg',
      linkedin: 'https://www.linkedin.com/in/katiagomez1/',
    },
    coords: [37.8044, -122.2712],
    accent: '#1565C0',
  },
  {
    name: 'Carolina Honduras Health Foundation',
    location: 'Winston-Salem, NC',
    focus: 'Medical, Dental & Vision Care',
    focusEs: 'Atención Médica, Dental y Visual',
    description:
      'Sends medical mission teams to rural Honduras, where its clinics provide nearly 10,000 free medical, dental, and vision visits each year—restoring health, delivering life-saving medications, and supporting families at no cost to them.',
    descriptionEs:
      'Envía equipos de misiones médicas a la Honduras rural, donde sus clínicas brindan cerca de 10,000 consultas médicas, dentales y visuales gratuitas cada año—restaurando la salud, entregando medicamentos que salvan vidas y apoyando a las familias sin costo alguno.',
    mission:
      'To bring healthcare and hope to Honduran communities with little to no access to care—one patient, one family, and one community at a time.',
    missionEs:
      'Llevar salud y esperanza a comunidades hondureñas con poco o ningún acceso a la atención médica—un paciente, una familia y una comunidad a la vez.',
    url: 'https://chhf.org/',
    donateUrl: 'https://secure.qgiv.com/for/websidfo/',
    logo: 'https://cdn.magicpatterns.com/uploads/7ozbaowPy8myoa4wiY69uE/Carolina_Honduras_Health_Foundation.png',
    director: {
      name: 'Suzanne LaVenture',
      title: 'Executive Director',
      titleEs: 'Directora Ejecutiva',
      photo:
        'https://cdn.magicpatterns.com/uploads/oiixgEEAsH4FKE4Mos1HSd/Suzanne_LaVenture.png',
      linkedin: 'https://www.linkedin.com/in/suzanne-laventure-19980866/',
    },
    coords: [36.0999, -80.2442],
    accent: '#1f3f73',
    partnerSites: [
      {
        name: 'Medical Mission Clinics',
        nameEs: 'Clínicas de Misiones Médicas',
        location: 'Department of Colón, Honduras',
        coords: [15.6333, -86.0],
      },
    ],
  },
  {
    name: 'Fundación Hondureña para el Niño con Cáncer',
    registered501c3: false,
    location: 'Tegucigalpa, Honduras',
    focus: 'Pediatric Cancer Care',
    focusEs: 'Atención del Cáncer Infantil',
    description:
      'For more than 40 years, the Foundation has led the way in offering free, comprehensive treatment for childhood cancer in Honduras—including the country’s first medical center dedicated to the integral care of pediatric oncology patients.',
    descriptionEs:
      'Por más de 40 años, la Fundación ha liderado el camino para ofrecer tratamiento integral y gratuito contra el cáncer infantil en Honduras—incluido el primer centro médico del país dedicado a la atención integral de pacientes oncológicos pediátricos.',
    mission:
      'To give every Honduran child with cancer the best chance at life—providing free oncology treatment, medication, and quality of life to the children and families fighting the disease.',
    missionEs:
      'Dar a cada niño hondureño con cáncer la mejor oportunidad de vida—brindando tratamiento oncológico gratuito, medicamentos y calidad de vida a los niños y familias que luchan contra la enfermedad.',
    url: 'https://salvamivida.org/',
    logo: 'https://cdn.magicpatterns.com/uploads/eEyGPgjLqCkPshcP8jEjaG/Fundacion_de_Nino_con_Cancer.png',
    director: {
      name: 'Nubia Mendoza',
      title: 'President',
      titleEs: 'Presidenta',
      photo:
        'https://cdn.magicpatterns.com/uploads/fkbTzojoDpujqFN8EGvW6q/Nubia_Mendoza.jpg',
    },
    coords: [14.0723, -87.1921],
    accent: '#cf142b',
  },
  {
    name: 'Hondurans in LA',
    registered501c3: false,
    location: 'Los Angeles, CA',
    logo: 'https://cdn.magicpatterns.com/uploads/rZCBaftZ8KANJXvdqHYmSs/Screenshot_2026-09-24_at_9.16.02_PM.png',
    // The supplied logo is a screenshot: crop away the surrounding page and
    // mask to the logo's outline so stray text and rules don't show.
    logoCrop: {
      region: [0, 9.6, 94.5, 82.6],
      aspect: 860 / 557,
      mask: 'polygon(22.7% 0.9%, 44.4% 4.8%, 40.9% 18.9%, 51.5% 18.9%, 53.3% 4.8%, 75.6% 4.8%, 82.8% 10.8%, 100% 99.3%, 1.2% 99.3%)',
    },
    focus: 'Culture, Community & Social Justice',
    focusEs: 'Cultura, Comunidad y Justicia Social',
    description:
      'A Los Angeles community organization connecting Honduran families with their roots through community work, cultural programming, and social justice—while fostering economic opportunity and building lasting partnerships across the city.',
    descriptionEs:
      'Una organización comunitaria de Los Ángeles que conecta a las familias hondureñas con sus raíces a través del trabajo comunitario, la programación cultural y la justicia social—mientras fomenta oportunidades económicas y construye alianzas duraderas en la ciudad.',
    mission:
      'To amplify and connect Los Angeles Hondurans with their roots—through community work, cultural exposure, and social justice—while fostering economic opportunity and building partnerships that strengthen our community today and for generations to come.',
    missionEs:
      'Amplificar y conectar a los hondureños de Los Ángeles con sus raíces—a través del trabajo comunitario, la exposición cultural y la justicia social—mientras fomentamos oportunidades económicas y construimos alianzas que fortalecen a nuestra comunidad hoy y por generaciones.',
    director: {
      name: 'Dolly Figueroa Ayala',
      title: 'Executive Director',
      titleEs: 'Directora Ejecutiva',
      photo:
        'https://cdn.magicpatterns.com/uploads/7EA5dcH78EcPk67rjEQ2Un/Dolly_Figueroa.png',
      linkedin: 'https://www.linkedin.com/in/dolly-figueroa-27507157/',
    },
    coords: [34.0522, -118.2437],
    accent: '#1565C0',
  },
  {
    name: 'Boston Honduran Reunion Committee',
    location: 'Boston, MA',
    focus: 'Culture & Community Celebration',
    focusEs: 'Cultura y Celebración Comunitaria',
    description:
      'For more than three decades, the Boston Honduran Reunion has brought Honduran families, friends, and communities together for a three-day cultural celebration—an opening night, a grand gala, and a family cultural day centered on heritage, unity, and connection.',
    descriptionEs:
      'Por más de tres décadas, la Reunión Hondureña de Boston ha unido a familias, amistades y comunidades hondureñas en una celebración cultural de tres días—una noche de apertura, una gran gala y un día cultural familiar centrados en la herencia, la unidad y la conexión.',
    mission:
      'From our roots to the future—connecting Honduran hearts through meaningful events and community support.',
    missionEs:
      'De nuestras raíces al futuro—conectando corazones hondureños a través de eventos significativos y apoyo comunitario.',
    url: 'https://bostonhrc.com/',
    logo: 'https://cdn.magicpatterns.com/uploads/7HwyfKHDKRcSonV2LuPsN6/BHRC_Logo.png',
    // The source has a cream background: lift it to pure white so only the
    // crest shows against the card.
    logoFilter: 'brightness(1.11) contrast(1.04)',
    director: {
      name: 'Venessa Merren-Taylor',
      title: 'Vice President',
      titleEs: 'Vicepresidenta',
      photo:
        'https://cdn.magicpatterns.com/uploads/q89uZKqxiwEsuCdn6V7nxo/Vanessa_Merren_Taylor.png',
      linkedin: 'https://www.linkedin.com/in/venessa-merren-taylor-1b218139/',
    },
    coords: [42.3601, -71.0589],
    accent: '#1f3f73',
  },
  {
    name: 'Amor y Fuerza Honduras',
    location: 'Miami, FL',
    focus: 'Relief & Community Support',
    focusEs: 'Ayuda y Apoyo Comunitario',
    description:
      'A 501(c)(3) nonprofit mobilizing volunteers, donors, and partners to deliver essential supplies, education support, and recovery programs to families across Honduras.',
    descriptionEs:
      'Una organización sin fines de lucro 501(c)(3) que moviliza voluntarios, donantes y aliados para llevar suministros esenciales, apoyo educativo y programas de recuperación a familias en todo Honduras.',
    mission:
      'To mobilize love into action—connecting volunteers, donors, and partners to bring essential supplies, education support, and recovery programs to the families who need them most.',
    missionEs:
      'Movilizar el amor en acción—conectando voluntarios, donantes y aliados para llevar suministros esenciales, apoyo educativo y programas de recuperación a las familias que más lo necesitan.',
    url: 'https://amoryfuerza.org/',
    donateUrl: 'https://amoryfuerza.org/get-involved/',
    logo: 'https://cdn.magicpatterns.com/uploads/9DBjs9zbsMWhEkuaqGhutA/1.jpg',
    director: {
      name: 'Martha Hernandez',
      title: 'Executive Director',
      titleEs: 'Directora Ejecutiva',
      photo:
        'https://cdn.magicpatterns.com/uploads/wGXjr34q1cN4MwqWHTo8Ft/Team-AF-20-scaled.jpg',
      linkedin: 'https://www.linkedin.com/in/martha-hernandez-1260202a/',
    },
    coords: [25.7617, -80.1918],
    accent: '#cf142b',
    partnerSites: [
      {
        name: 'Community Programs',
        nameEs: 'Programas Comunitarios',
        location: 'Choloma, Cortés',
        coords: [15.6144, -87.9528],
      },
      {
        name: 'Community Programs',
        nameEs: 'Programas Comunitarios',
        location: 'Tegucigalpa, Francisco Morazán',
        coords: [14.0723, -87.1921],
      },
    ],
  },
]
